import { useState, useCallback, useEffect, useMemo } from 'react'
import { exportCode } from '../api'
import CommonProps from '../common/props.common'
import { showSimpleError, showSimpleSuccess } from '@/lib/toast'

const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `${filename}.zip`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  window.URL.revokeObjectURL(url)
}

export const useExportFlow = (uniqueUrl, projectName, onClose, isAiCompanionMode = false) => {
  const [exportFormat, setExportFormat] = useState(isAiCompanionMode ? 'sourceCode' : 'html')
  const [exportAllPages, setExportAllPages] = useState(isAiCompanionMode)
  const [exportFileName, setExportFileName] = useState(projectName || '')
  const [downloadableAssets, setDownloadableAssets] = useState(false)
  const [isExporting, setIsExporting] = useState(false)

  useEffect(() => {
    if (isAiCompanionMode) {
      setExportFormat((current) => (current === 'deployment' ? current : 'sourceCode'))
      setExportAllPages(true)
      setDownloadableAssets(false)
    }
  }, [isAiCompanionMode])

  useEffect(() => {
    setExportFileName(projectName || '')
    setDownloadableAssets(false)
    setExportFormat(isAiCompanionMode ? 'sourceCode' : 'html')
    setExportAllPages(isAiCompanionMode)
  }, [projectName, isAiCompanionMode])

  const extras = useMemo(() => {
    const base = { filename: exportFileName || projectName || 'export', props: CommonProps }
    if (isAiCompanionMode) {
      return {
        ...base,
        exportPurpose: exportFormat,
        exportFramework: 'react',
        exportAllPages: true,
        downloadableAssets: false,
      }
    }
    return {
      ...base,
      exportFramework: exportFormat === 'react' ? 'react' : 'htmlcss',
      exportAllPages,
      downloadableAssets,
    }
  }, [exportFileName, projectName, isAiCompanionMode, exportFormat, exportAllPages, downloadableAssets])

  const exportProject = useCallback(async () => {
    if (!uniqueUrl) {
      showSimpleError('Project URL not found')
      return
    }

    setIsExporting(true)
    try {
      const blob = await exportCode('home', uniqueUrl, extras)
      downloadBlob(blob, extras.filename)
      showSimpleSuccess('Exported successfully!')
      onClose?.()
    } catch (error) {
      showSimpleError(error.message || 'Failed to export code')
    } finally {
      setIsExporting(false)
    }
  }, [uniqueUrl, extras, isAiCompanionMode, exportFormat, onClose])

  return {
    exportFormat,
    setExportFormat,
    exportAllPages,
    setExportAllPages,
    exportFileName,
    setExportFileName,
    downloadableAssets,
    setDownloadableAssets,
    isExporting,
    exportProject,
  }
}

